import React, { useState, useEffect, useCallback } from 'react';
import './styles/App.css';
import getCssTemplate from './templates/CssTemplate';
import getHtmlTemplate from './templates/HtmlTemplate';
import { minifyCss, minifyHtml } from './utils/MinifyUtils';
import LottiePreview from './components/LottiePreview';
import BotConfig from './components/BotConfig';
import DataSources from './components/DataSources';
import Dashboard from './components/Dashboard'; // Import the Dashboard component
import Login from './components/Login'; // Import the Login component
import UserSettings from './components/UserSettings'; // Import the UserSettings component
import TestAndDeploy from './components/TestAndDeploy'; // Import the new component
import ApiService from './utils/ApiService'; // Import the API service
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import SkeletonLoader from './components/SkeletonLoader'; // Import the SkeletonLoader component
// Font Awesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faCode, faExpand, faTimes, faPaintBrush, faRobot, faUserCog, faChevronLeft, faChevronRight, faInfoCircle, faCog, faCheckCircle, faCopy, faRocket, faDatabase, faHome, faSignOutAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

// Function to convert hex to RGB
const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [0, 0, 0];
};

// HelpTooltip Component
const HelpTooltip: React.FC<{text: string}> = ({ text }) => (
  <span className="help-tooltip-container">
    <FontAwesomeIcon icon={faInfoCircle} className="help-icon" />
    <span className="help-tooltip-text">{text}</span>
  </span>
);

// App Component
const App: React.FC = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userData, setUserData] = useState<any>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [isGoToLoginHovered, setIsGoToLoginHovered] = useState(false);
  
  // CSS Colors
  const [mainColor, setMainColor] = useState('#F4A460'); // Light orange/sandy brown
  const [darkColor, setDarkColor] = useState('#D2691E'); // Chocolate brown
  const [tooltipTextColor, setTooltipTextColor] = useState('#F5F5DC'); // Beige
  const [cssOutput, setCssOutput] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Color Presets
  const colorPresets = [
    { name: "Default Orange", main: "#F4A460", dark: "#D2691E", tooltip: "#F5F5DC" },
    { name: "Modern Blue", main: "#4A90E2", dark: "#2A5885", tooltip: "#FFFFFF" },
    { name: "Professional Gray", main: "#607D8B", dark: "#455A64", tooltip: "#FFFFFF" },
    { name: "Friendly Green", main: "#66BB6A", dark: "#388E3C", tooltip: "#F5F5F5" }
  ];
  
  // Lottie Animation Options
  const lottieAnimations = [
    { name: "CLAI v.1", url: "https://lottie.host/ee28a3f4-4cff-4774-b97a-52d5ff24abd8/hTlZOksOC5.lottie" },
    { name: "CLAI v.2", url: "https://lottie.host/cc56af3d-f540-4a26-9204-9f4332c125cf/XlgAuqCvXk.lottie" },
  ];
  
  // Position States
  const [bottomPosition, setBottomPosition] = useState('30px');
  const [rightPosition, setRightPosition] = useState('30px');

  // HTML Fields
  const [lottieUrl, setLottieUrl] = useState(lottieAnimations[0].url);
  const [tooltipText, setTooltipText] = useState('👋 Hello! How can I help you today? Click here to start chatting with me.');
  const [baseUrl, setBaseUrl] = useState('https://clai-chatbot-954627004317.us-central1.run.app');
  const [projectName, setProjectName] = useState('clai-new-project');
  const [trace, setTrace] = useState('true');
  // const [embed, setEmbed] = useState('true');
  const [embedOptions, setEmbedOptions] = useState('hide_loading_screen');
  const [htmlOutput, setHtmlOutput] = useState('');


  // Sidebar and Active View State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // Start with sidebar open
  const [activeView, setActiveView] = useState('pluginSetup'); // Default view
  
  
  // Active tab (CSS or HTML)
  const [activeTab, setActiveTab] = useState('css');

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);
  
  // Replace legacy auth check with Firebase Auth
  useEffect(() => {
    const auth = getAuth(); // Ensure getAuth is imported from 'firebase/auth'
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // setIsCheckingAuth(true); // Already true by default, will be set to false at the end
      if (user) {
        if (user.emailVerified) {
          // User is authenticated with Firebase and email is verified
          setIsAuthenticated(true); // Set authentication status
          try {
            const userInfo = await ApiService.getCurrentUser();
            setUserData(userInfo);
            setGlobalError(null); // Clear any previous errors if ApiService call is successful
          } catch (error: any) {
            setUserData(null); // Clear user data on API error
            console.error("ApiService.getCurrentUser failed after successful Firebase auth:", error);
            if (error instanceof Error && error.message.includes('Our service is temporarily unavailable')) {
              setGlobalError(error.message);
            } else {
              // Set a more generic error message for other API failures
              setGlobalError("Failed to load your profile information. Please check your connection or try again later.");
            }
            // isAuthenticated remains true because Firebase auth itself was successful and email verified.
          }
        } else {
          // User is authenticated with Firebase but email is NOT verified
          setIsAuthenticated(false); // Treat as not authenticated for app access
          setUserData(null);
          // Display a message prompting the user to verify their email.
          // Login.tsx also shows a message, this serves as a fallback or for page refreshes.
          setGlobalError("Please verify your email address to log in. Check your inbox for a verification link.");
        }
      } else {
        // User is signed out from Firebase
        setIsAuthenticated(false);
        setUserData(null);
        setGlobalError(null); // Clear any global errors on sign-out
      }
      setIsCheckingAuth(false); // Indicate that authentication check is complete
    });
    return () => unsubscribe();
  }, []);
  
  // Handle login success
  const handleLogin = () => {
    // This function is called from Login.tsx AFTER email verification is confirmed for login attempts.
    // Or after a new sign-up where verification is now required before this is hit.
    const auth = getAuth();
    const user = auth.currentUser;
    if (user && user.emailVerified) { // Double-check emailVerified, though Login.tsx should ensure this.
      ApiService.getCurrentUser()
        .then(userInfo => {
          setUserData(userInfo);
          setGlobalError(null); // Clear error on successful data fetch
        })
        .catch(error => {
          console.error("ApiService.getCurrentUser failed in handleLogin:", error);
          setUserData(null);
          setGlobalError("Failed to load your profile information after login. Please try refreshing.");
        });
    }
    // If user is somehow not verified here, onAuthStateChanged should ideally catch it,
    // but as a safeguard, we only proceed if verified.
    // The primary role of handleLogin is to transition view and confirm state.
    if (user && user.emailVerified) {
        setIsAuthenticated(true);
        setActiveView('dashboard'); // Redirect to dashboard after login
    } else if (user && !user.emailVerified) {
        // This case should ideally not be hit if Login.tsx logic is correct
        // and onAuthStateChanged handles unverified users.
        setIsAuthenticated(false);
        setGlobalError("Please verify your email before logging in.");
        setActiveView('login'); // Stay on or return to login
    }
    // If no user, onAuthStateChanged will handle setting isAuthenticated to false.
  };
  
  // Handle logout
  const handleLogout = () => {
    const auth = getAuth();
    auth.signOut();
    setIsAuthenticated(false);
    setUserData(null);
  };
  
  // Memoize your functions with useCallback
  const generateCss = useCallback(() => {
    const mainRgb = hexToRgb(mainColor);
    const darkRgb = hexToRgb(darkColor);
    const css = getCssTemplate(mainColor, darkColor, mainRgb, darkRgb, tooltipTextColor);
    setCssOutput(minifyCss(css));
  }, [mainColor, darkColor, tooltipTextColor]);
  
  const generateHtml = useCallback(() => {
    const html = getHtmlTemplate(lottieUrl, tooltipText, baseUrl, projectName, trace, embedOptions);
    setHtmlOutput(minifyHtml(html));
  }, [lottieUrl, tooltipText, baseUrl, projectName, trace, embedOptions]);

  // Update your useEffect
  useEffect(() => {
    if (mainColor && darkColor && lottieUrl && tooltipText) {
      generateCss();
      generateHtml();
    }
  }, [mainColor, darkColor, lottieUrl, tooltipText, baseUrl, projectName, trace, embedOptions, generateCss, generateHtml]);
  
  const [showTooltip, setShowTooltip] = useState(false);
  const [showWindow, setShowWindow] = useState(false);

  const toggleTooltip = () => {
    setShowTooltip(!showTooltip);
  };

  const toggleWindow = () => {
    setShowWindow(!showWindow);
    setShowTooltip(false);
  };

  // Toggle Sidebar Function
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  // Create download link for CSS
  const downloadCss = () => {
    const element = document.createElement('a');
    const file = new Blob([cssOutput], { type: 'text/css' });
    element.href = URL.createObjectURL(file);
    element.download = 'clai-style.min.css';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };
  
  // Create download link for HTML
  const downloadHtml = () => {
    const element = document.createElement('a');
    const file = new Blob([htmlOutput], { type: 'text/html' });
    element.href = URL.createObjectURL(file);
    element.download = 'clai-template.html';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // If still checking authentication status, show loading
  if (isCheckingAuth) {
    return <SkeletonLoader />;
  }

  // Show global error if backend is unreachable
  if (globalError) {
    const isVerificationError = globalError.toLowerCase().includes('verify your email');

    const goToLoginButtonBaseStyle: React.CSSProperties = {
      background: 'transparent',
      color: darkColor, // Use state variable
      border: `2px solid ${darkColor}`, // Use state variable
      borderRadius: 6,
      padding: '10px 30px',
      fontSize: 16,
      fontWeight: 'bold',
      cursor: 'pointer',
      boxShadow: 'none',
      transition: 'background-color 0.3s ease, color 0.3s ease'
    };

    const goToLoginButtonHoverStyle: React.CSSProperties = {
      ...goToLoginButtonBaseStyle,
      background: darkColor, // Use state variable
      color: '#fff',
    };

    return (
      <div className="error-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#fff8f6', padding: '20px' }}>
        <div style={{
          background: '#fff',
          padding: '30px 40px',
          borderRadius: '8px',
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center'
        }}>
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" color={darkColor} style={{ marginBottom: 24 }} />
          <h1 style={{ color: darkColor, marginBottom: 12, fontSize: '1.8em' }}>{isVerificationError ? "Email Verification Required" : "Service Unavailable"}</h1>
          <p style={{ color: '#333', fontSize: 18, lineHeight: '1.6', maxWidth: 400, textAlign: 'center', marginBottom: 32, marginInline: 'auto' }}>{globalError}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            {!isVerificationError && (
              <button
                style={{
                  background: `linear-gradient(135deg, ${mainColor}, ${darkColor})`, // Use state variables
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '12px 32px',
                  fontSize: 16,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(212,105,30,0.08)',
                  width: '100%',
                  maxWidth: '250px'
                }}
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            )}
            {(isVerificationError || activeView !== 'login') && (
              <button
                style={isGoToLoginHovered ? goToLoginButtonHoverStyle : goToLoginButtonBaseStyle}
                onMouseEnter={() => setIsGoToLoginHovered(true)}
                onMouseLeave={() => setIsGoToLoginHovered(false)}
                onClick={() => {
                  setGlobalError(null); 
                  setIsAuthenticated(false); 
                  setActiveView('login'); 
                  const auth = getAuth();
                  if (auth.currentUser) {
                    auth.signOut();
                  }
                }}
              >
                Go back to login page
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // If not authenticated, show only the Login component
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    // 1. Apply dynamic class to the root container based on sidebar state
    <div className={`app-container ${isSidebarOpen ? 'sidebar-open' : ''}`}>

      {/* 2. Sidebar Component */}
      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          {isSidebarOpen ? (
            <img src="./clai_editor_logo.png" alt="CLAI Logo" className="sidebar-logo" />
          ) : (
            <img src="./logo512.png" alt="CLAI Logo" className="sidebar-logo" />
          )}
        </div>
        <nav className="sidebar-nav">
          <ul>
            {/* Dashboard Link */}
            <li className={activeView === 'dashboard' ? 'active' : ''}>
              <button onClick={() => setActiveView('dashboard')}>
                <FontAwesomeIcon icon={faHome} /> <span>Dashboard</span>
              </button>
            </li>
            {/* Plugin Setup Link */}
            <li className={activeView === 'pluginSetup' ? 'active' : ''}>
              <button onClick={() => setActiveView('pluginSetup')}>
                <FontAwesomeIcon icon={faCog} /> <span>Plugin Setup</span>
              </button>
            </li>
            {/* Bot Configuration Link */}
            <li className={activeView === 'botConfig' ? 'active' : ''}>
              <button onClick={() => setActiveView('botConfig')}>
                <FontAwesomeIcon icon={faRobot} /> <span>Bot Configuration</span>
              </button>
            </li>
            {/* Test and Deploy Link - Ensure this was the intended spot or adjust as needed */}
            <li className={activeView === 'testDeploy' ? 'active' : ''}>
              <button onClick={() => setActiveView('testDeploy')}>
                <FontAwesomeIcon icon={faRocket} /> <span>Test and Deploy</span>
              </button>
            </li>
            {/* Data Sources Link */}
            <li className={activeView === 'dataSources' ? 'active' : ''}>
              <button onClick={() => setActiveView('dataSources')}>
                <FontAwesomeIcon icon={faDatabase} /> <span>Data Sources</span>
              </button>
            </li>
          </ul>
        </nav>
        <div className="sidebar-footer">
          <ul>
            {/* User Settings Link */}
            <li className={activeView === 'userSettings' ? 'active' : ''}>
              <button onClick={() => setActiveView('userSettings')}>
                <FontAwesomeIcon icon={faUserCog} /> <span>User Settings</span>
              </button>
            </li>
            {/* Logout Button */}
            <li>
              <button onClick={handleLogout} className="logout-btn">
                <FontAwesomeIcon icon={faSignOutAlt} /> <span>Logout</span>
              </button>
            </li>
          </ul>
          {/* Toggle Button */}
          <button onClick={toggleSidebar} className="sidebar-toggle-btn">
            <FontAwesomeIcon icon={isSidebarOpen ? faChevronLeft : faChevronRight} />
            <span>{isSidebarOpen ? ' Collapse' : ''}</span>
          </button>
        </div>
      </div>

      {/* 3. Main Content Area */}
      <div className="main-content">
        {/* Remove the user welcome message from here as we'll display it only in the Dashboard */}
        
        {/* Remove the button to open sidebar when collapsed since sidebar is always visible now */}

        {/* 4. Conditional Rendering based on activeView */}
        {activeView === 'pluginSetup' && (
          // The original layout for Plugin Setup goes here
          <div className="main-layout">
            {/* Configuration Panel */}
            <div className="config-panel">
              {/* Add a title for the Plugin Setup page */}
              <h1 className="section-title">Plugin Setup</h1>
              
              {/* Tab Navigation */}
              <div className="tab-navigation">
                <button 
                  className={`tab-btn ${activeTab === 'css' ? 'active' : ''}`}
                  onClick={() => setActiveTab('css')}
                >
                  <FontAwesomeIcon icon={faPaintBrush} className="icon-left" /> CSS Configuration
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'html' ? 'active' : ''}`}
                  onClick={() => setActiveTab('html')}
                >
                  <FontAwesomeIcon icon={faCode} className="icon-left" /> HTML Configuration
                </button>
              </div>

              {/* CSS Configuration Section */}
              {activeTab === 'css' && (
                <>
                  {/* Color Presets UI */}
                  <div className="color-presets">
                    <h3>Color Presets</h3>
                    <div className="preset-buttons">
                      {colorPresets.map((preset, index) => (
                        <button 
                          key={index}
                          className="preset-button"
                          onClick={() => {
                            setMainColor(preset.main);
                            setDarkColor(preset.dark);
                            setTooltipTextColor(preset.tooltip);
                          }}
                          style={{ 
                            background: `linear-gradient(135deg, ${preset.main}, ${preset.dark})`,
                            color: preset.tooltip
                          }}
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="color-pickers">
                    <div className="color-picker">
                      <label htmlFor="main-color">
                        Main Color (Light)
                        <HelpTooltip text="This color will be used for the chatbot button background and UI accents." />
                      </label>
                      <input 
                        type="color" 
                        id="main-color" 
                        value={mainColor} 
                        onChange={(e) => setMainColor(e.target.value)} 
                      />
                      <input 
                        type="text" 
                        value={mainColor} 
                        onChange={(e) => setMainColor(e.target.value)}
                      />
                    </div>
                    
                    <div className="color-picker">
                      <label htmlFor="dark-color">
                        Dark Color
                        <HelpTooltip text="This color will be used for gradient effects and darker UI elements. Choose a darker shade of your main color for best results." />
                      </label>
                      <input 
                        type="color" 
                        id="dark-color" 
                        value={darkColor} 
                        onChange={(e) => setDarkColor(e.target.value)} 
                      />
                      <input 
                        type="text" 
                        value={darkColor} 
                        onChange={(e) => setDarkColor(e.target.value)}
                      />
                    </div>
                    
                    <div className="color-picker">
                      <label htmlFor="tooltip-text-color">
                        Tooltip Text Color
                        <HelpTooltip text="The color of text in the tooltip. Choose a color with good contrast against your selected main/dark colors." />
                      </label>
                      <input 
                        type="color" 
                        id="tooltip-text-color" 
                        value={tooltipTextColor} 
                        onChange={(e) => setTooltipTextColor(e.target.value)} 
                      />
                      <input 
                        type="text" 
                        value={tooltipTextColor} 
                        onChange={(e) => setTooltipTextColor(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="position-controls-container">
                  <div className='position-controls'>
                    <label htmlFor='tooltip-text-color'>Bottom</label>
                    <input
                      type='number'
                      value={bottomPosition.replace('px', '')}
                      onChange={(e) => setBottomPosition(`${e.target.value}px`)}
                    />
                  </div>
                  <div className='position-controls'>
                    <label htmlFor='tooltip-text-color'>Right</label>
                    <input
                      type='number'
                      value={rightPosition.replace('px', '')}
                      onChange={(e) => setRightPosition(`${e.target.value}px`)}
                    />
                  </div>
                  </div>
                </>
              )}

              {/* HTML Configuration Section */}
              {activeTab === 'html' && (
                <div className="html-config">
                  <div className="form-group">
                    <label htmlFor="lottieAnimation">
                      Animation
                      <HelpTooltip text="Select an animation that will appear in the chat button. Choose one that matches your website's theme and purpose." />
                    </label>
                    <select
                      id="lottieAnimation"
                      value={lottieUrl}
                      onChange={(e) => setLottieUrl(e.target.value)}
                    >
                      {lottieAnimations.map((animation, index) => (
                        <option key={index} value={animation.url}>
                          {animation.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="tooltipText">
                      Tooltip Text
                      <HelpTooltip text="This welcome message appears in a tooltip when users first see the chatbot. Keep it concise, friendly and inviting." />
                    </label>
                    <input 
                      type="text" 
                      id="tooltipText" 
                      value={tooltipText}
                      onChange={(e) => setTooltipText(e.target.value)} 
                      placeholder="Enter tooltip text"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="baseUrl">
                      Base URL
                      <HelpTooltip text="The URL where your CLAI backend is hosted. Don't change this unless you're using a custom deployment." />
                    </label>
                    <input 
                      type="text" 
                      id="baseUrl" 
                      value={baseUrl}
                      onChange={(e) => setBaseUrl(e.target.value)} 
                      placeholder="Enter base URL"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="projectName">
                      Project Name
                      <HelpTooltip text="A unique identifier for your chatbot. This helps track analytics and configure your bot's behavior." />
                    </label>
                    <input 
                      type="text" 
                      id="projectName" 
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)} 
                      placeholder="Enter project name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="trace">
                      Trace
                      <HelpTooltip text="Enable/disable debugging info. Set to 'false' for production environments unless troubleshooting an issue." />
                    </label>
                    <select 
                      id="trace" 
                      value={trace}
                      onChange={(e) => setTrace(e.target.value)}
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="embedOptions">
                      Embed Options
                      <HelpTooltip text="Additional options for embedding the chatbot, e.g., 'hide_loading_screen'." />
                    </label>
                    <input 
                      type="text" 
                      id="embedOptions" 
                      value={embedOptions}
                      onChange={(e) => setEmbedOptions(e.target.value)} 
                      placeholder="Enter embed options"
                    />
                  </div>
                </div>
              )}

              {/* Output Container (CSS/HTML) */}
              {(cssOutput || htmlOutput) && (
                <div className="output-container">
                  {cssOutput && activeTab === 'css' && (
                    <>
                      <h2 className="center-title">Generated CSS</h2>
                      <div className="css-output">
                        <pre>{cssOutput}</pre>
                      </div>
                      <div className="button-group">
                        <button className="download-button" onClick={downloadCss}>
                          <FontAwesomeIcon icon={faDownload} /> Download CSS
                        </button>
                        <button
                          className="copy-button"
                          onClick={() => {
                            navigator.clipboard.writeText(cssOutput);
                            setCopySuccess(true); // Show confirmation
                            setTimeout(() => setCopySuccess(false), 2000); // Hide after 2 seconds
                          }}
                        >
                          <FontAwesomeIcon icon={faCopy} /> Copy CSS
                        </button>
                      </div>
                      {/* Floating Notification */}
                      {copySuccess && (
                        <div className="floating-notification">
                          <FontAwesomeIcon icon={faCheckCircle} className="notification-icon" />
                          CSS copied to clipboard!
                        </div>
                      )}
                    </>
                  )}
                  
                  {htmlOutput && activeTab === 'html' && (
                    <>
                      <h2 className='center-title'>Generated HTML</h2>
                      <div className="html-output">
                        <pre>{htmlOutput}</pre>
                      </div>
                      <div className="button-group">
                        <button className="download-button" onClick={downloadHtml}>
                          <FontAwesomeIcon icon={faDownload} /> Download HTML
                        </button>
                        <button
                          className="copy-button"
                          onClick={() => {
                            navigator.clipboard.writeText(htmlOutput);
                            setCopySuccess(true); // Show confirmation
                            setTimeout(() => setCopySuccess(false), 2000); // Hide after 2 seconds
                          }}
                        >
                          <FontAwesomeIcon icon={faCopy} /> Copy HTML
                        </button>
                      </div>
                      {/* Floating Notification */}
                      {copySuccess && (
                        <div className="floating-notification">
                          <FontAwesomeIcon icon={faCheckCircle} className="notification-icon" />
                          HTML copied to clipboard!
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* How to Use Button & Dialog */}
              <button className="open-dialog-button" onClick={openDialog}>
                <FontAwesomeIcon icon={faInfoCircle} className="icon-left" /> How to Use Generated Code
              </button>
              {isDialogOpen && (
                <div className="dialog-overlay">
                  <div className="dialog">
                    <h2>How to Use the Generated Code</h2>
                    <ol className="tutorial-steps">
                      <li>
                        <div className="step-header">
                          <FontAwesomeIcon icon={faCog} className="step-icon" />
                          <span>Go to <strong>Settings</strong> in Wix's Studio</span>
                        </div>
                        <p>Scroll to the <strong>Development & Integrations</strong> section and click on <strong>Custom Code</strong>.</p>
                        <img 
                          src="./WixTutorial/development_integrations.png" 
                          alt="Wix Settings" 
                          className="dialog-image" 
                        />
                      </li>
                      <li>
                        <div className="step-header">
                          <FontAwesomeIcon icon={faPaintBrush} className="step-icon" />
                          <span> Add a Custom Code in the <strong>Head</strong></span>
                        </div>
                        <p>Name it <strong>"Chatbot-Style"</strong>, paste your generated CSS into the code space, and save it.</p>
                        <img 
                          src="./WixTutorial/custom_css.png" 
                          alt="Add Chatbot Style" 
                          className="dialog-image" 
                        />
                      </li>
                      <li>
                        <div className="step-header">
                          <FontAwesomeIcon icon={faCode} className="step-icon" />
                          <span> Add a Custom Code in the <strong>Body-End</strong></span>
                        </div>
                        <p>Name it <strong>"Chatbot"</strong>, paste your generated HTML into the code space, and save it.</p>
                        <img 
                          src="./WixTutorial/custom_html.png"
                          alt="Add Chatbot HTML" 
                          className="dialog-image" 
                        />
                      </li>
                      <li>
                        <div className="step-header">
                          <FontAwesomeIcon icon={faCheckCircle} className="step-icon" />
                          <span>You're Done!</span>
                        </div>
                        <p>CLAI is now part of your website!</p>
                      </li>
                    </ol>
                    {/* Video Tutorial */}
                    <div className="tutorial-video">
                      <h3>Watch the Full Tutorial</h3>
                      <div className='video-container'>
                        <iframe 
                          src="https://drive.google.com/file/d/1JtKs-kN6MbGtJtBT3xyojMvqoOHJBi54/preview" 
                          title="Tutorial Video" 
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                    <button className="close-dialog-button" onClick={closeDialog}>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div> {/* End of config-panel */}

            {/* Preview Panel */}
            <div className="preview-panel">
              <h2>Live Preview</h2>
              <div className="preview-container">
                <div className="mockup-screen">
                  <div className="mockup-content">
                    <p>Your website content would appear here</p>
                    <p>The chatbot plugin appears on the bottom right</p>
                  </div>
                  
                  {/* Preview of the chatbot plugin */}
                  <div className="chatbot-preview"
                    style={{
                      position: 'absolute',
                      bottom: bottomPosition,
                      right: rightPosition,
                    }}>
                    {showWindow && (
                      <div 
                        className="preview-floating-window"
                        style={{
                          boxShadow: `0 10px 25px rgba(0, 0, 0, 0.15), 0 5px 10px rgba(0, 0, 0, 0.12)`
                        }}
                      >
                        <div 
                          className="preview-controls"
                          style={{
                            background: `linear-gradient(to bottom, 
                              ${darkColor} 0%, 
                              rgba(${hexToRgb(darkColor).join(', ')}, 0.95) 30%,
                              rgba(${Math.floor((hexToRgb(darkColor)[0] + hexToRgb(mainColor)[0]) / 2)}, 
                                  ${Math.floor((hexToRgb(darkColor)[1] + hexToRgb(mainColor)[1]) / 2)}, 
                                  ${Math.floor((hexToRgb(darkColor)[2] + hexToRgb(mainColor)[2]) / 2)}, 0.9) 50%,
                              rgba(${Math.floor((hexToRgb(darkColor)[0] + hexToRgb(mainColor)[0] * 2) / 3)}, 
                                  ${Math.floor((hexToRgb(darkColor)[1] + hexToRgb(mainColor)[1] * 2) / 3)}, 
                                  ${Math.floor((hexToRgb(darkColor)[2] + hexToRgb(mainColor)[2] * 2) / 3)}, 0.7) 70%,
                              rgba(${hexToRgb(mainColor).join(', ')}, 0.4) 85%,
                              rgba(${hexToRgb(mainColor).join(', ')}, 0) 100%)`
                          }}
                        >
                          <div className="preview-dragbar">
                            <div className="preview-left-controls">
                              <button className="preview-close-button" onClick={toggleWindow}>
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                            <div className="preview-right-controls">
                              <button className="preview-resize-button">
                                <FontAwesomeIcon icon={faExpand} />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="preview-iframe-content">
                          <div className="placeholder-content">
                            <p>Chatbot interface would appear here</p>
                            <p>URL: {baseUrl}</p>
                            <p>Project: {projectName}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <button 
                      className="preview-open-button" 
                      style={{
                        background: `linear-gradient(135deg, ${mainColor}, ${darkColor})`,
                        boxShadow: `0 0 10px rgba(${hexToRgb(mainColor).join(', ')}, 0.5), 
                                   0 0 20px rgba(${hexToRgb(mainColor).join(', ')}, 0.3),
                                   0 0 30px rgba(${hexToRgb(mainColor).join(', ')}, 0.3), 
                                   0 0 40px rgba(${hexToRgb(mainColor).join(', ')}, 0.2)`,
                        display: showWindow ? 'none' : 'flex'
                      }}
                      onClick={toggleWindow}
                    >
                      <div className="preview-lottie-placeholder">
                        <LottiePreview url={lottieUrl} />
                      </div>
                    </button>
                    
                    {showTooltip && !showWindow && (
                      <div 
                        className="preview-tooltip"
                        style={{
                          background: `linear-gradient(135deg, ${mainColor}, ${darkColor})`,
                          border: `2px solid ${mainColor}`,
                          boxShadow: `0 0 10px rgba(${hexToRgb(mainColor).join(', ')}, 0.5), 
                                    0 0 20px rgba(${hexToRgb(mainColor).join(', ')}, 0.3), 
                                    0 0 30px rgba(${hexToRgb(mainColor).join(', ')}, 0.3), 
                                    0 0 40px rgba(${hexToRgb(mainColor).join(', ')}, 0.2)`,
                          animation: 'tooltipEntrance 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55), tooltipPulse 2s infinite ease-in-out',
                          color: tooltipTextColor
                        }}
                      >
                        {tooltipText}
                        <button className="preview-tooltip-close" onClick={toggleTooltip}>
                          <FontAwesomeIcon icon={faTimes} size="xs" />
                        </button>
                        <div 
                          className="preview-tooltip-arrow"
                          style={{
                            borderColor: `transparent transparent transparent ${mainColor}`
                          }}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="preview-controls-panel">
                  <button 
                    className={`preview-control-btn ${showTooltip ? 'active' : ''}`} 
                    onClick={toggleTooltip}
                    disabled={showWindow}
                  >
                    {showTooltip ? 'Hide Tooltip' : 'Show Tooltip'}
                  </button>
                  <button 
                    className={`preview-control-btn ${showWindow ? 'active' : ''}`} 
                    onClick={toggleWindow}
                  >
                    {showWindow ? 'Hide Chat Window' : 'Show Chat Window'}
                  </button>
                </div>
              </div>
            </div> {/* End of preview-panel */}

          </div> // End of main-layout
        )} {/* End of activeView === 'pluginSetup' */}

        {/* Bot Configuration View */}
        {activeView === 'botConfig' && (
          <div className="main-layout" style={{ gridTemplateColumns: "1fr", maxWidth: "1800px", margin: "0 auto", padding: "0 20px" }}>
            <div className="config-panel">
              <h1 className="section-title">Bot Configuration</h1>
              <BotConfig />
            </div>
          </div>
        )}

        {/* Test and Deploy View */}
        {activeView === 'testDeploy' && (
          <div className="main-layout" style={{ gridTemplateColumns: "1fr", maxWidth: "1800px", margin: "0 auto", padding: "0 20px" }}>
            <div className="config-panel">
              <h1 className="section-title">Test and Deploy</h1>
              <TestAndDeploy />
            </div>
          </div>
        )}

        {/* Data Sources View */}
        {activeView === 'dataSources' && (
          <div className="main-layout" style={{ gridTemplateColumns: "1fr", maxWidth: "1800px", margin: "0 auto", padding: "0 20px" }}>
            <div className="config-panel">
              <h1 className="section-title">Data Sources</h1>
              <DataSources />
            </div>
          </div>
        )}

        {/* User Settings View */}
        {activeView === 'userSettings' && (
          <div className="main-layout" style={{ gridTemplateColumns: "1fr", maxWidth: "1800px", margin: "0 auto", padding: "0 20px" }}>
            <div className="config-panel">
              <UserSettings />
            </div>
          </div>
        )}

        {/* Dashboard View */}
        {activeView === 'dashboard' && (
          <div className="main-layout" style={{ gridTemplateColumns: "1fr", maxWidth: "1800px", margin: "0 auto", padding: "0 20px" }}>
            <div className="config-panel">
              <Dashboard />
            </div>
          </div>
        )}

      </div> {/* End of main-content */}
    </div> // End of app-container
  );
};

export default App;